import { usePage } from "@inertiajs/react"
import { LazyMotion } from "motion/react"
import type { ReactNode } from "react"
import { useEffect } from "react"

import { FlashToaster } from "@/components/flash-toaster"
import { SiteHeader } from "@/components/site-header"
import { syncTheme } from "@/hooks/use-appearance"
import { isAppSurface } from "@/lib/app-surfaces"
import { loadMotionFeatures } from "@/lib/motion-features"

interface PersistentLayoutProps {
  children: ReactNode
}

// Some marketing surfaces own their own chrome and must not get the shared
// SiteHeader on top:
//   • Working with Robots renders its own themed header per surface (dark on
//     felt pages, none on the play table).
//   • The AI consulting funnel's quiz + result surfaces (/services/ai/*)
//     render their own chrome; the landing page itself is a marketing page
//     and takes the shared header.
//   • Discovery briefs (/services/discovery/*) are link-shared, one-to-one
//     client documents with a full-bleed Dark Lime hero; they deliberately
//     carry no marketing nav so the prospect stays in the brief.
//   • The Quick email gate (/quick/<slug>) fronts a link-shared prospect
//     document; it's a modal over the (stand-in) page, so no nav either.
//     The /quick index keeps its chrome — only sub-paths match.
function isSelfChromed(url: string): boolean {
  const path = url.split("?")[0]
  return (
    path === "/working-with-robots" ||
    path.startsWith("/working-with-robots/") ||
    path.startsWith("/services/ai/") ||
    path.startsWith("/services/discovery/") ||
    path.startsWith("/quick/")
  )
}

export default function PersistentLayout({ children }: PersistentLayoutProps) {
  const { url } = usePage()
  // App surfaces own their own chrome (auth shell, app shell, settings) and
  // follow the appearance preference; everything else is the marketing site,
  // which gets the SiteHeader and stays pinned to its light surface.
  const showHeader = !isAppSurface(url) && !isSelfChromed(url)

  // Appearance is route-scoped, so crossing the app/marketing boundary via
  // client-side navigation must re-evaluate the theme.
  useEffect(() => {
    syncTheme()
  }, [url])

  return (
    <LazyMotion features={loadMotionFeatures}>
      {showHeader && (
        <a
          href="#main-content"
          className="focus:bg-background focus:text-foreground focus:ring-ring sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:rounded-md focus:px-4 focus:py-3 focus:font-sans focus:text-sm focus:shadow-lg focus:ring-2"
        >
          Skip to content
        </a>
      )}
      {showHeader && <SiteHeader />}
      {children}
      <FlashToaster />
    </LazyMotion>
  )
}
